import { useRef, useState } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import {
  AlarmEvent,
  Events,
  TimelineDataWithLoadingState,
  TimelineFetchFilters,
  TimelineProductionBatchesAndCounts,
} from '../../../../../interfaces/timeline';
import { MachineStatisticOEETeep } from '@interfaces/machines';
import { getMachineAlarmEvent, getMachineEventTimeline, getMachineProductionBatches } from '@reducers/machines';
import { localTime, formatStartOf, getUTCFormatOfLocalTime } from '@helpers';
import { machineStatisticsOeeTeep } from '@reducers/statistics';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { TIMELINE_BATCH_SELECT, EMPTY_EVENTS } from '@components/machines/components/timeline/constants';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { useEnhancedBatches } from './useEnhancedBatches';
import { useTimelineSelectors } from './useTimelineSelectors';
import { useRealtimeTimelineUpdates } from './useRealtimeTimelineUpdates';
import { processTimelineResponse } from '@components/machines/components/timeline/helpers/mergeTimelineEvents';

export interface UseTimelineViewModelParams {
  startTime: string;
  endTime: string;
  yearIsEnabled: boolean;
}

export const useTimelineViewModel = ({ startTime, endTime, yearIsEnabled }: UseTimelineViewModelParams) => {
  const dispatch = useAppDispatch();

  const { selectedMachine } = useTimelineSelectors();

  const { timePeriod: selectedTime } = useHandleParams();

  const [events, setEvents] = useState<Events>(EMPTY_EVENTS);

  const [productionBatchesAndCounts, setProductionBatchesAndCounts] = useState<TimelineDataWithLoadingState<TimelineProductionBatchesAndCounts>>({
    loaded: false,
    data: { batches: [], counts: [] },
  });

  const [alarmEventGroups, setAlarmEventGroups] = useState<TimelineDataWithLoadingState<AlarmEvent[][]>>({
    loaded: false,
    data: [],
  });

  const [machineStatistics, setMachineStatistics] = useState<TimelineDataWithLoadingState<MachineStatisticOEETeep | null>>({
    loaded: false,
    data: null,
  });

  const { enhancedBatchesData } = useEnhancedBatches({
    batchesData: { loaded: productionBatchesAndCounts.loaded, data: productionBatchesAndCounts.data?.batches || [] },
    events,
  });

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const { fetchTimelineWithRealtime } = useRealtimeTimelineUpdates();

  const abortControllerEvents = useRef<AbortController>();
  const abortControllerProductionBatches = useRef<AbortController>();
  const abortControllerAlarmEvent = useRef<AbortController>();
  const abortControllerMachineStatistics = useRef<AbortController>();
  const cancelRealtimeRef = useRef<(() => void) | null>(null);

  const getTimeLine = ({ start, end }: TimelineFetchFilters) => {
    setEvents(EMPTY_EVENTS);

    cancelRealtimeRef.current?.();
    if (abortControllerEvents.current) {
      abortControllerEvents.current.abort();
    }
    abortControllerEvents.current = new AbortController();

    const utcStart = getUTCFormatOfLocalTime(start);
    const utcEnd = getUTCFormatOfLocalTime(end);

    cancelRealtimeRef.current = fetchTimelineWithRealtime({
      machineId: selectedMachine?.id,
      from: utcStart,
      until: utcEnd,
      thunk: getMachineEventTimeline(
        { id: selectedMachine?.id, start: utcStart, end: utcEnd, useAsyncFlowForRealtimeQuery: true },
        abortControllerEvents.current.signal
      ),
      processResponse: processTimelineResponse,
      onInitialData: (data) => {
        setEvents({ loaded: true, ...data });
      },
      onRealtimeData: (data) => {
        setEvents({ loaded: true, ...data });
      },
      getCurrentEvents: () => eventsRef.current,
    });
  };

  const getProductionBatches = async ({ start, end }: TimelineFetchFilters) => {
    setProductionBatchesAndCounts({ loaded: false, data: { batches: [], counts: [] } });

    if (abortControllerProductionBatches.current) {
      abortControllerProductionBatches.current.abort();
    }
    abortControllerProductionBatches.current = new AbortController();

    const data = await dispatch(
      getMachineProductionBatches(
        {
          id: selectedMachine?.id,
          start: getUTCFormatOfLocalTime(start),
          end: getUTCFormatOfLocalTime(end),
          select: TIMELINE_BATCH_SELECT,
        },
        abortControllerProductionBatches.current.signal
      )
    );

    setProductionBatchesAndCounts({ loaded: true, data });
  };

  const getAlarmEvent = async ({ start, end }: TimelineFetchFilters) => {
    setAlarmEventGroups({ loaded: false, data: [] });

    if (abortControllerAlarmEvent.current) {
      abortControllerAlarmEvent.current.abort();
    }
    abortControllerAlarmEvent.current = new AbortController();

    const data = await dispatch(
      getMachineAlarmEvent(
        {
          id: selectedMachine?.id,
          start: getUTCFormatOfLocalTime(start),
          end: getUTCFormatOfLocalTime(end),
          productionBatchId: null,
        },
        abortControllerAlarmEvent.current.signal
      )
    );

    setAlarmEventGroups({ loaded: true, data });
  };

  const getMachineStatistics = async ({ start, end }: TimelineFetchFilters) => {
    if (localTime(end).diff(localTime(start), selectedTime) !== 1) {
      setMachineStatistics({ loaded: true, data: null });
      return;
    }

    setMachineStatistics({ loaded: false, data: null });

    if (abortControllerMachineStatistics.current) {
      abortControllerMachineStatistics.current.abort();
    }
    abortControllerMachineStatistics.current = new AbortController();

    const data = await dispatch(
      machineStatisticsOeeTeep(
        {
          date: localTime(start).startOf(formatStartOf(selectedTime)).format(DATE_FORMAT),
          period: selectedTime,
          machineId: selectedMachine?.id,
        },
        abortControllerMachineStatistics.current.signal
      )
    );

    if (data?.machineStatistics) {
      setMachineStatistics({ loaded: true, data: data.machineStatistics?.[0] });
    }
  };

  const fetchData = async (params?: { start?: string; end?: string }) => {
    const { start = startTime, end = endTime } = params || {};
    if (selectedMachine?.id && (yearIsEnabled || selectedTime !== TimePeriod.YEAR)) {
      const fetchParams = { start, end };
      const results = await Promise.allSettled([
        getTimeLine(fetchParams),
        getProductionBatches(fetchParams),
        getMachineStatistics(fetchParams),
        getAlarmEvent(fetchParams),
      ]);
      // Log any rejected promises for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const requestNames = ['getTimeLine', 'getProductionBatches', 'getMachineStatistics', 'getAlarmEvent'];
          console.error(`${requestNames[index]} failed:`, result.reason);
        }
      });
    }
  };

  return {
    fetchData,
    abortControllerEvents,
    abortControllerProductionBatches,
    abortControllerAlarmEvent,
    abortControllerMachineStatistics,
    alarmEventGroups,
    events,
    productionBatchesAndCounts,
    machineStatistics,
    enhancedBatchesData,
  };
};
